// Expandable per-platform Cash Positions matrix - ports reporting-hub's
// CashPositionsMatrix.tsx. `entry` is the single `type: "cash_positions"`
// calculation entry (see cash_positions.py / CashPositionsEntry in
// reporting-hub/frontend/src/lib/types.ts): one row per platform with
// platform_cash/spv_cash/total (each a PositionValue: status + value + note)
// and an optional `steps` breakdown (sign/label/value) shown on row click.

import React, { useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { formatCurrency, titleCasePlatform } from './calcFormat';

const STATUS_LABEL = {
  ok: '',
  missing_upload: 'Not uploaded',
  not_implemented: 'Not implemented',
  error: 'Error',
};

const HeaderTooltip = ({ label, formula, targetClass }) => (
  <span
    data-pr-tooltip={formula}
    data-pr-position="top"
    className={`${targetClass} cr-help-underline`}
  >
    {label}
  </span>
);

const ValueCell = ({ value }) => {
  if (!value) return <span className="cr-muted-value">{'–'}</span>;
  if (value.status === 'ok' && value.value !== null) {
    return <span>{formatCurrency(value.value)}</span>;
  }
  return (
    <span className={`cr-status-note${value.status === 'error' ? ' is-error' : ''}`} title={value.note || undefined}>
      {STATUS_LABEL[value.status] || '–'}
    </span>
  );
};

const CashPositionsMatrix = ({ entry }) => {
  const [openPlatforms, setOpenPlatforms] = useState(new Set());

  if (!entry || !entry.platforms) return null;

  const toggle = (platform) => {
    setOpenPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  return (
    <div className="cr-table-frame">
      <Tooltip target=".cr-cashpos-th-tooltip" />
      <table className="cr-data-table">
        <thead>
          <tr>
            <th>Originator</th>
            <th className="cr-right">
              <HeaderTooltip
                label="Originator Cash"
                targetClass="cr-cashpos-th-tooltip"
                formula="Cash held at the Originator itself - calculation differs per Originator, see the steps for this row (click to expand)"
              />
            </th>
            <th className="cr-right">
              <HeaderTooltip
                label="SPV Cash"
                targetClass="cr-cashpos-th-tooltip"
                formula="SUM(Cash Movement Amount) WHERE Effective Date <= Loanbook last date"
              />
            </th>
            <th className="cr-right">
              <HeaderTooltip label="Total" targetClass="cr-cashpos-th-tooltip" formula="Originator Cash + SPV Cash" />
            </th>
            <th style={{ width: '1.5rem' }} />
          </tr>
        </thead>
        <tbody>
          {entry.platforms.map((row) => {
            const hasSteps = !!row.steps && row.steps.length > 0;
            const isOpen = openPlatforms.has(row.platform);
            return (
              <React.Fragment key={row.platform}>
                <tr
                  onClick={() => hasSteps && toggle(row.platform)}
                  className={isOpen ? 'is-open' : undefined}
                  style={{ cursor: hasSteps ? 'pointer' : undefined }}
                >
                  <td className="cr-strong-cell">{titleCasePlatform(row.platform)}</td>
                  <td className="cr-right">
                    <ValueCell value={row.platform_cash} />
                  </td>
                  <td className="cr-right">
                    <ValueCell value={row.spv_cash} />
                  </td>
                  <td className="cr-right cr-strong-cell">
                    <ValueCell value={row.total} />
                  </td>
                  <td className="cr-right cr-chevron-cell">
                    {hasSteps && (
                      <i
                        className={`pi pi-chevron-right cr-row-chevron${isOpen ? ' is-open' : ''}`}
                      />
                    )}
                  </td>
                </tr>
                {isOpen && hasSteps && (
                  <tr>
                    <td colSpan={5} className="cr-expand-cell">
                      <table className="cr-steps-table" style={{ maxWidth: '28rem' }}>
                        <tbody>
                          {row.steps.map((step, i) => (
                            <tr key={`${step.label}-${i}`}>
                              <td className="cr-step-label">
                                ({step.sign}) {step.label}
                              </td>
                              <td className="cr-right">{formatCurrency(step.value)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td className="cr-step-total">
                              = {titleCasePlatform(row.platform)} Originator Cash
                            </td>
                            <td className="cr-right cr-step-total">
                              {row.platform_cash && row.platform_cash.value !== null
                                ? formatCurrency(row.platform_cash.value)
                                : '–'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CashPositionsMatrix;
